import React, { useMemo, useState } from 'react';
import {
  FiCheckCircle, FiXCircle, FiFileText, FiDownload, FiEdit2, FiCheck, FiMessageSquare,
} from 'react-icons/fi';
import { BsCash } from 'react-icons/bs';

function fmt(amount, currency = 'ZAR') {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency }).format(amount);
}

const DOC_TYPE_LABELS = {
  supplier_invoices: 'Invoice',
  purchase_listing: 'Purchase Listing',
  supplier_statements: 'Statement',
  company_supplier_ledgers: 'Ledger',
  remittance_advice: 'Remittance',
  bank_statements: 'Bank Statement',
};

function formatDate(dateStr) {
  if (!dateStr) return null;
  try { return new Date(dateStr).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return dateStr; }
}

function SupplierCard({ match, allDocs, onDownload, onPaymentDecision, onSupplierNoteChange, saving }) {
  const [customAmount, setCustomAmount] = useState('');
  const [editingCustom, setEditingCustom] = useState(false);
  // Note prompt state: { type: 'rejected'|'custom', amount?: number } | null
  const [notePrompt, setNotePrompt] = useState(null);
  const [noteText, setNoteText] = useState('');
  // Free-text note, independent of the payment_note reason prompt above — always editable,
  // saved on blur, and shown read-only on the external share link.
  const [supplierNote, setSupplierNote] = useState(match.supplier_note || '');

  const linkedDocs = useMemo(() => {
    const ids = new Set(match.document_ids || []);
    const DOC_ORDER = { supplier_invoices: 0, purchase_listing: 1 };
    return (allDocs || [])
      .filter(d => ids.has(d.id))
      .sort((a, b) => {
        const ta = a.user_doc_type || a.detected_doc_type || a.document_type;
        const tb = b.user_doc_type || b.detected_doc_type || b.document_type;
        return (DOC_ORDER[ta] ?? 99) - (DOC_ORDER[tb] ?? 99);
      });
  }, [match.document_ids, allDocs]);

  const ps = match.payment_status;

  const handleCustomSave = () => {
    const val = parseFloat(customAmount);
    if (!isNaN(val) && val >= 0) {
      setEditingCustom(false);
      setNotePrompt({ type: 'custom', amount: val });
      setNoteText('');
    }
  };

  const startCustom = () => {
    setCustomAmount(match.payment_amount != null ? String(match.payment_amount) : String(match.variance || ''));
    setEditingCustom(true);
  };

  const handleNoteSave = () => {
    if (!noteText.trim()) return;
    if (notePrompt.type === 'rejected') {
      onPaymentDecision(match.id, 'rejected', null, noteText.trim());
    } else if (notePrompt.type === 'custom') {
      onPaymentDecision(match.id, 'custom', notePrompt.amount, noteText.trim());
    }
    setNotePrompt(null);
    setNoteText('');
  };

  const handleSupplierNoteBlur = () => {
    const trimmed = supplierNote.trim();
    if (trimmed === (match.supplier_note || '')) return;
    onSupplierNoteChange?.(match.id, trimmed || null);
  };

  return (
    <div className="border rounded-lg overflow-hidden bg-green-50 border-green-200">
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <FiCheckCircle className="w-5 h-5 flex-shrink-0 text-green-600" />
          <div className="min-w-0">
            <p className="font-medium text-gray-900 truncate">{match.supplier_name}</p>
            <span className="text-xs font-medium text-green-600">Accepted</span>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-sm font-semibold text-gray-900">
            {fmt(match.payment_amount ?? match.variance)}
          </p>
          {match.payment_amount != null && match.variance != null && match.payment_amount !== match.variance ? (
            <p className="text-xs text-orange-600 font-medium">
              {`-${parseFloat(((1 - match.payment_amount / match.variance) * 100).toFixed(2))}% (${fmt(match.variance - match.payment_amount)} discount)`}
            </p>
          ) : (
            <p className="text-xs text-gray-500">amount to pay</p>
          )}
        </div>
      </div>

      {/* Linked documents */}
      {linkedDocs.length > 0 && (
        <div className="border-t border-green-100 bg-white">
          <div className="px-4 py-2 border-b border-gray-50">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Linked Documents</p>
          </div>
          <div className="divide-y divide-gray-50">
            {linkedDocs.map(doc => {
              const effType = doc.user_doc_type || doc.detected_doc_type || doc.document_type;
              const typeLabel = DOC_TYPE_LABELS[effType] || effType || 'Document';
              const amount = doc.user_amount != null ? doc.user_amount : doc.amount;
              return (
                <React.Fragment key={doc.id}>
                  <div className="px-4 py-2.5 flex items-center gap-3">
                    <FiFileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium text-gray-700 truncate">{doc.file_name}</span>
                        <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded flex-shrink-0">{typeLabel}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                        {doc.supplier_name && <span className="text-xs text-gray-500 truncate">{doc.supplier_name}</span>}
                        {doc.period_date && <span className="text-xs text-gray-400">{formatDate(doc.period_date)}</span>}
                        {amount != null && <span className="text-xs font-medium text-gray-700">{fmt(amount)}</span>}
                      </div>
                    </div>
                    {doc.source !== 'exchange' && (
                      <button onClick={() => onDownload(doc)} className="p-1.5 text-gray-400 hover:text-green-600 transition-colors flex-shrink-0" title="Download">
                        <FiDownload className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {/* Multi-invoice breakdown — shown when a single PDF contains multiple invoices */}
                  {effType === 'supplier_invoices' && (() => {
                    const invLines = (doc.ai_raw?.line_items || []).filter(li => li.type === 'invoice' && li.amount != null);
                    if (invLines.length <= 1) return null;
                    return (
                      <div className="mx-4 mb-2 mt-0.5">
                        <div className="flex items-center gap-2 px-2 py-0.5 text-xs text-gray-400 font-medium border-b border-orange-100 mb-0.5">
                          <span className="w-28 flex-shrink-0">Invoice #</span>
                          <span className="w-24 flex-shrink-0">Date</span>
                          <span className="flex-1 text-right">Amount</span>
                        </div>
                        {invLines.map((li, i) => (
                          <div key={i} className="flex items-center gap-2 px-2 py-1 rounded text-xs bg-orange-50 text-gray-600">
                            <span className="w-28 flex-shrink-0 text-orange-700 font-medium truncate">{li.ref || '—'}</span>
                            <span className="w-24 flex-shrink-0 text-gray-400">{li.date || '—'}</span>
                            <span className="flex-1 text-right font-medium text-gray-700">
                              {li.amount != null ? fmt(li.amount) : '—'}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* Payment decision bar */}
      <div className="border-t border-green-100 bg-green-50 px-4 py-3 space-y-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs font-medium text-gray-600">Payment decision</p>
          {ps ? (
            <div className="flex items-center gap-2 flex-wrap">
              {ps === 'rejected' ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 px-2 py-1 rounded-lg">
                  <FiXCircle className="w-3.5 h-3.5" /> Not paying
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 border border-green-300 px-2 py-1 rounded-lg">
                  <FiCheckCircle className="w-3.5 h-3.5" />
                  {ps === 'custom' ? `Paying ${fmt(match.payment_amount)}` : `Paying ${fmt(match.variance)}`}
                </span>
              )}
              <button onClick={() => { onPaymentDecision(match.id, null, null, null); setEditingCustom(false); }} disabled={saving}
                className="text-xs text-gray-400 hover:text-gray-600 underline">change</button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => onPaymentDecision(match.id, match.payment_amount != null ? 'custom' : 'accepted', match.payment_amount ?? null, null)}
                disabled={saving}
                className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                <FiCheckCircle className="w-3.5 h-3.5" />
                Pay {match.payment_amount != null ? fmt(match.payment_amount) : fmt(match.variance)}
              </button>
              {editingCustom ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    className="w-28 text-xs border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    value={customAmount}
                    onChange={e => setCustomAmount(e.target.value)}
                    placeholder="Amount"
                    autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') handleCustomSave(); if (e.key === 'Escape') setEditingCustom(false); }}
                  />
                  <button onClick={handleCustomSave} disabled={saving}
                    className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                    <FiCheck className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => setEditingCustom(false)} className="text-xs text-gray-400 hover:text-gray-600 px-1">✕</button>
                </div>
              ) : (
                <button onClick={startCustom} disabled={saving}
                  className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 bg-white border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50 transition-colors">
                  <FiEdit2 className="w-3.5 h-3.5" /> Set amount
                </button>
              )}
              <button
                onClick={() => { setNotePrompt({ type: 'rejected' }); setNoteText(''); }}
                disabled={saving}
                className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 bg-white border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
              >
                <FiXCircle className="w-3.5 h-3.5" /> Not paying
              </button>
            </div>
          )}
        </div>

        {/* Note prompt (shown inline after Not paying or Set amount) */}
        {notePrompt && (
          <div className="bg-white border border-amber-200 rounded-lg p-3 space-y-2">
            <p className="text-xs font-medium text-gray-700">
              {notePrompt.type === 'rejected'
                ? 'Reason for not paying (required)'
                : `Reason for paying ${fmt(notePrompt.amount)} instead of ${fmt(match.variance)} (required)`}
            </p>
            <textarea
              autoFocus
              rows={2}
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="Enter reason..."
              className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400 resize-none"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setNotePrompt(null)} className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1">Cancel</button>
              <button
                onClick={handleNoteSave}
                disabled={!noteText.trim()}
                className="text-xs font-medium px-3 py-1 bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-40 transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        )}

        {/* Show saved note */}
        {ps && match.payment_note && (
          <div className="flex items-start gap-1.5 text-xs text-gray-500">
            <FiMessageSquare className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>{match.payment_note}</span>
          </div>
        )}

        {/* Free-text note to supplier — always editable here, visible read-only on the
            external share link so the approvals team can see context without being able
            to change it. Separate from payment_note above, which is a required reason tied
            to a specific reject/custom-amount decision. */}
        <div className="pt-1">
          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-1">
            <FiMessageSquare className="w-3.5 h-3.5 flex-shrink-0" /> Note
          </label>
          <textarea
            rows={1}
            value={supplierNote}
            onChange={e => setSupplierNote(e.target.value)}
            onBlur={handleSupplierNoteBlur}
            placeholder="Add a note for this supplier (visible to the approvals team)..."
            className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-green-400 resize-none"
          />
        </div>
      </div>
    </div>
  );
}

function SummaryCards({ summary, currency, totalPayable }) {
  if (!summary) return null;
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="bg-white border border-gray-200 rounded-lg px-4 py-3">
        <div className="flex items-center gap-2 mb-1">
          <FiCheckCircle className="w-4 h-4 text-green-600" />
          <p className="text-xs text-gray-500">Total Suppliers</p>
        </div>
        <p className="text-lg font-semibold text-gray-900">{summary.totalSuppliers}</p>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg px-4 py-3">
        <div className="flex items-center gap-2 mb-1">
          <BsCash className="w-4 h-4 text-amber-600" />
          <p className="text-xs text-gray-500">Total Amount to Pay</p>
        </div>
        <p className="text-lg font-semibold text-gray-900">{fmt(totalPayable ?? summary.totalOutstanding, currency)}</p>
      </div>
    </div>
  );
}

function generateSummaryPDF({ matches, session, shareTokens = [], currency = 'ZAR', totalPayable, currentUserName }) {
  const fmtPDF = (amount) => {
    if (amount == null) return '—';
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency }).format(amount);
  };
  const fmtDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  // Build a match-id → accepted_by_name map from share tokens.
  // included_match_ids === null means the token covers ALL matches in the session.
  const matchAcceptedBy = {};
  for (const t of shareTokens) {
    if (!t.accepted_by_name) continue;
    if (t.included_match_ids === null) {
      // Covers all matches — set as default for every match
      for (const m of matches) {
        if (!matchAcceptedBy[m.id]) matchAcceptedBy[m.id] = t.accepted_by_name;
      }
    } else if (Array.isArray(t.included_match_ids)) {
      for (const mid of t.included_match_ids) {
        if (!matchAcceptedBy[mid]) matchAcceptedBy[mid] = t.accepted_by_name;
      }
    }
  }
  // Fallback for matches not covered by any token (internally accepted)
  const internalAcceptor = currentUserName || '—';

  const payableMatches = matches.filter(m => m.payment_status !== 'rejected');
  const periodLabel = [session?.period_start && fmtDate(session.period_start), session?.period_end && fmtDate(session.period_end)].filter(Boolean).join(' – ');
  const generatedOn = fmtDate(new Date().toISOString());

  const rows = payableMatches.map(m => {
    const amount = m.payment_status === 'custom' ? m.payment_amount : (m.payment_amount ?? m.variance);
    const hasDiscount = m.payment_amount != null && m.variance != null && m.payment_amount !== m.variance;
    const discountPct = hasDiscount ? parseFloat(((1 - m.payment_amount / m.variance) * 100).toFixed(2)) : null;
    const acceptedBy = matchAcceptedBy[m.id] || internalAcceptor;
    return { name: m.supplier_name, amount, discountPct, acceptedBy };
  }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const tableRows = rows.map(r => `
    <tr>
      <td class="supplier">${r.name || ''}</td>
      <td class="amount">${fmtPDF(r.amount)}</td>
      <td class="discount">${r.discountPct != null ? `-${r.discountPct}%` : ''}</td>
      <td class="accepted">${r.acceptedBy}</td>
    </tr>`).join('');

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${session?.name || 'Recon Summary'}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 10pt; color: #1a1a2e; background: #fff; padding: 24px 28px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 6px; }
  .header-left h1 { font-size: 17pt; font-weight: 700; color: #1e3a5f; letter-spacing: -0.3px; }
  .header-right { text-align: right; font-size: 8pt; color: #666; line-height: 1.6; }
  .divider { height: 3px; background: linear-gradient(90deg, #1e3a5f 0%, #4a90d9 100%); border-radius: 2px; margin-bottom: 10px; }
  .meta { font-size: 8.5pt; color: #555; margin-bottom: 14px; display: flex; gap: 20px; }
  .meta span { display: flex; align-items: center; gap: 4px; }
  .meta strong { color: #1e3a5f; }
  table { width: 100%; border-collapse: collapse; }
  thead tr { background: #1e3a5f; }
  thead th { padding: 8px 10px; text-align: left; font-size: 9pt; font-weight: 600; color: #fff; letter-spacing: 0.3px; white-space: nowrap; }
  thead th.amount, thead th.discount { text-align: right; }
  thead th.accepted { white-space: nowrap; }
  tbody tr:nth-child(even) { background: #f0f5fb; }
  tbody tr:hover { background: #e8f0fa; }
  tbody td { padding: 5.5px 10px; font-size: 9.5pt; border-bottom: 1px solid #dde5f0; vertical-align: middle; }
  td.supplier { min-width: 160px; }
  td.amount { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; font-weight: 500; }
  td.discount { text-align: right; white-space: nowrap; color: #b45309; font-size: 8.5pt; font-weight: 500; }
  td.accepted { white-space: nowrap; color: #1e3a5f; font-size: 9pt; }
  .total-section { margin-top: 10px; display: flex; justify-content: flex-end; }
  .total-box { background: #1e3a5f; color: #fff; border-radius: 6px; padding: 8px 16px; font-size: 11pt; font-weight: 700; white-space: nowrap; }
  .footer { margin-top: 18px; font-size: 7.5pt; color: #999; border-top: 1px solid #e0e6ed; padding-top: 6px; display: flex; justify-content: space-between; }
  @page { size: A4 portrait; margin: 14mm 14mm; }
</style>
</head>
<body>
  <div class="header">
    <div class="header-left">
      <h1>${session?.name || 'Recon Summary'}</h1>
    </div>
    <div class="header-right">
      ${periodLabel ? `<div><strong>Period:</strong> ${periodLabel}</div>` : ''}
      <div><strong>Generated:</strong> ${generatedOn}</div>
      <div><strong>Suppliers:</strong> ${rows.length}</div>
    </div>
  </div>
  <div class="divider"></div>
  <table>
    <thead>
      <tr>
        <th class="supplier">Supplier</th>
        <th class="amount">Amount</th>
        <th class="discount">Discount</th>
        <th class="accepted">Accepted By</th>
      </tr>
    </thead>
    <tbody>${tableRows}</tbody>
  </table>
  <div class="total-section">
    <div class="total-box">Total payable: ${fmtPDF(totalPayable)}</div>
  </div>
  <div class="footer">
    <span>Generated by Doxolve</span>
    <span>${generatedOn}</span>
  </div>
</body>
</html>`;

  const pdfTitle = session?.name || 'Recon Summary';
  const prevTitle = document.title;

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.top = '-9999px';
  iframe.style.left = '-9999px';
  iframe.style.width = '210mm';
  iframe.style.height = '297mm';
  document.body.appendChild(iframe);
  iframe.contentDocument.open();
  iframe.contentDocument.write(html);
  iframe.contentDocument.close();
  iframe.contentWindow.focus();
  setTimeout(() => {
    document.title = pdfTitle;
    iframe.contentWindow.print();
    setTimeout(() => {
      document.title = prevTitle;
      document.body.removeChild(iframe);
    }, 1000);
  }, 300);
}

function exportPaymentCSV(payments, sessionName) {
  const rows = [
    ['Supplier', 'Bank Name', 'Bank Account', 'Branch Code', 'Amount'],
    ...payments.map(p => [p.supplier_name, p.bank_name, p.bank_account, p.bank_branch, p.amount]),
  ];
  const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `payment-${sessionName || 'recon'}-${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function SupplierReconReport({ reportData, onDownloadDoc, onPaymentDecision, onSupplierNoteChange, onAcceptRecon, acceptingRecon, matches: liveMatches, shareTokens = [], currentUserName }) {
  const { matches: reportMatches = [], docs = [], summary, currency = 'ZAR', session } = reportData || {};

  // Use live match data (from parent state) so payment_status updates reflect immediately
  const matches = useMemo(() => {
    if (!liveMatches) return reportMatches;
    const liveMap = Object.fromEntries(liveMatches.map(m => [m.id, m]));
    return reportMatches.map(m => liveMap[m.id] || m);
  }, [reportMatches, liveMatches]);

  const sortedMatches = useMemo(() =>
    [...matches].sort((a, b) => (a.supplier_name || '').localeCompare(b.supplier_name || '')),
  [matches]);

  // Live total: rejected → 0, custom → payment_amount, otherwise use payment_amount if pre-set by discount, else variance
  const totalPayable = useMemo(() =>
    sortedMatches.reduce((s, m) => {
      if (m.payment_status === 'rejected') return s;
      if (m.payment_status === 'custom') return s + (parseFloat(m.payment_amount) || 0);
      return s + (parseFloat(m.payment_amount ?? m.variance) || 0);
    }, 0),
  [sortedMatches]);

  if (!reportData) {
    return (
      <div className="text-center py-12 text-gray-400">
        <p className="text-sm">No report data available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SummaryCards summary={summary} currency={currency} totalPayable={totalPayable} />

      {/* Per-Supplier Results */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-medium text-gray-900">
            Supplier Results
            <span className="ml-2 text-sm font-normal text-gray-500">({sortedMatches.length})</span>
          </h3>
        </div>

        {sortedMatches.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            No accepted matches. Accept matches on the Matches tab to include them in the report.
          </div>
        ) : (
          <div className="space-y-3">
            {sortedMatches.map(match => (
              <SupplierCard
                key={match.id}
                match={match}
                allDocs={docs}
                onDownload={onDownloadDoc}
                onPaymentDecision={onPaymentDecision}
                onSupplierNoteChange={onSupplierNoteChange}
                saving={false}
              />
            ))}
          </div>
        )}
      </div>

      {/* Accept Recon button — only when all suppliers have a payment decision */}
      {sortedMatches.length > 0 && sortedMatches.every(m => m.payment_status != null) && !session?.recon_accepted && (
        <div className="flex justify-end pt-2 border-t border-gray-200">
          <button
            onClick={() => onAcceptRecon(sortedMatches, totalPayable)}
            disabled={acceptingRecon}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors shadow-sm"
          >
            <BsCash className="w-4 h-4" />
            {acceptingRecon ? 'Processing…' : 'Accept Recon & Generate Payment File'}
          </button>
        </div>
      )}

      {session?.recon_accepted && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-sm text-green-800">
            <FiCheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
            Recon accepted — payment file was generated.
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => generateSummaryPDF({ matches: sortedMatches, session, shareTokens, currency, totalPayable, currentUserName })}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white border border-green-300 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
            >
              <FiFileText className="w-3.5 h-3.5" />
              Payment Summary PDF
            </button>
            <button
              onClick={() => onAcceptRecon(sortedMatches, totalPayable)}
              disabled={acceptingRecon}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white border border-green-300 text-green-700 rounded-lg hover:bg-green-100 disabled:opacity-50 transition-colors"
            >
              <FiDownload className="w-3.5 h-3.5" />
              {acceptingRecon ? 'Generating…' : 'Re-download Payment File'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
